/**
 * The one place the dashboard talks to Firebase. Everything else takes plain domain
 * types from `@altrtrack/core`, mirroring the rule that keeps `store.ts` alone on the
 * bot side.
 */

import { initializeApp } from "firebase/app";
import { connectAuthEmulator, getAuth, signInWithCustomToken } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { firebaseConfig } from "./firebase-config.js";

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
/**
 * Callable functions (B-48: `reviewPlan`). Not pointed at an emulator in emulator mode —
 * nothing runs the functions emulator — so an injury review during offline work reaches
 * production with an emulator token, is refused, and the plan shows the review as not
 * having run. That is the failure path, and it is worth being able to see.
 */
export const functions = getFunctions(app, "asia-south1");

/**
 * Emulator mode, for working on the interface itself.
 *
 * `npm run dev` talks to PRODUCTION Firestore deliberately (B-13) — verifying the bot
 * against real data is the whole point. But laying out a screen needs data on it, and
 * signing in needs the live magic-link path to be working, so `VITE_EMULATOR=1 npm run
 * dev` points everything at the local emulators instead. `dashboard/dev/seed.mjs` fills
 * them and prints a token to sign in with.
 *
 * Vite substitutes `import.meta.env.VITE_EMULATOR` literally at build time, so with the
 * flag unset the whole block is `if (undefined)` and the minifier removes it — the
 * emulator connection and the sign-in helper are not in the deployed bundle at all.
 * Note the dot access: `import.meta.env["VITE_EMULATOR"]` is NOT substituted, and the
 * branch survives into production looking exactly as correct as this one.
 */
if (import.meta.env.VITE_EMULATOR) {
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  (globalThis as Record<string, unknown>)["__altrtrackDevSignIn"] = (token: string) =>
    signInWithCustomToken(auth, token);
}

/** Where a magic-link token is exchanged for a custom token (B-11). */
export const EXCHANGE_URL =
  "https://asia-south1-altrtrack.cloudfunctions.net/exchangeMagicLink";
